import React, { useState, useEffect } from 'react';
import { auth } from '../config/firebase';
import { signInAnonymously } from 'firebase/auth';
import firebaseService from '../services/firebaseService';
import { useNavigate } from 'react-router-dom';
import '../styles/components.css';

const RoomPreview = ({ roomId, onJoin, error: parentError }) => {
    const [roomInfo, setRoomInfo] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [playerName, setPlayerName] = useState(() => {
        return localStorage.getItem('guesswhat_username') || '';
    });
    const navigate = useNavigate();

    useEffect(() => {
        let unsubscribe;

        const fetchRoomInfo = async () => {
            try {
                // Subscribe to the game to get room info
                unsubscribe = firebaseService.subscribeToGame(roomId, (game) => {
                    if (game) {
                        // Check if the current user is already in the game
                        const currentUserId = auth.currentUser?.uid;
                        const isPlayerInGame = currentUserId && game.players &&
                            Object.values(game.players).some(p => p.id === currentUserId);

                        if (isPlayerInGame) {
                            // If player is in game, trigger the game start
                            onJoin(game);
                        } else {
                            // Otherwise, just update the room info
                            setRoomInfo({
                                playerCount: Object.keys(game.players || {}).length,
                                maxPlayers: game.settings?.maxPlayers || 8,
                                status: game.status
                            });
                        }
                    } else {
                        setError('Game not found');
                    }
                    setIsLoading(false);
                });
            } catch (error) {
                console.error('Error fetching room info:', error);
                setError('Failed to fetch room info');
                setIsLoading(false);
            }
        };

        fetchRoomInfo();

        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [roomId, onJoin]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmedName = playerName.trim();

        if (!trimmedName) {
            setError('Please enter your name');
            return;
        }

        try {
            localStorage.setItem('guesswhat_username', trimmedName);

            // Ensure user is authenticated
            if (!auth.currentUser) {
                await signInAnonymously(auth);
            }

            const player = {
                id: auth.currentUser.uid,
                name: trimmedName
            };

            await firebaseService.joinGame(roomId, player);

            // The game update will be handled by the subscription in useEffect
        } catch (err) {
            console.error('Error joining game:', err);
            setError(err.message || 'Failed to join game');
        }
    };

    const handleCreateNewGame = async () => {
        try {
            // Create game through API
            const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/games`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    playerName: 'Anonymous',
                    playerId: auth.currentUser?.uid || 'temp'
                })
            });

            if (!response.ok) {
                throw new Error('Failed to create game');
            }

            const { roomId } = await response.json();
            navigate(`/room/${roomId}`);
        } catch (error) {
            console.error('Error creating new game:', error);
            setError('Failed to create new game');
        }
    };

    if (isLoading) {
        return <div className="loading">Loading room info...</div>;
    }

    if (error || parentError) {
        return (
            <div className="error-container">
                <div className="error-message">{error || parentError}</div>
            </div>
        );
    }

    if (!roomInfo) {
        return <div className="error-message">Room not found</div>;
    }

    return (
        <>
            <div className="floating-shapes">
                <div className="shape"></div>
                <div className="shape"></div>
                <div className="shape"></div>
            </div>
            <div className="landing-content">
                <h1 className="game-title">
                    Guess What?!
                </h1>
                <p className="game-subtitle">AI images, wild guesses, and endless fun! Compete with friends to crack the prompt!</p>
                <div className="room-info">
                    <div className="room-code-display">
                        <span className="room-code-label">Room<strong>{roomId}</strong>
                        </span>
                        <span className="player-count">
                            {roomInfo.playerCount} players
                        </span>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="form-container">
                    <div className="input-group">
                        <label htmlFor="playerName">Your Name</label>
                        <input
                            type="text"
                            id="playerName"
                            value={playerName}
                            onChange={(e) => setPlayerName(e.target.value)}
                            placeholder="Enter your name"
                            maxLength={20}
                        />
                    </div>
                    <button
                        type="submit"
                        className="primary-button"
                        disabled={!playerName.trim() || roomInfo.playerCount >= roomInfo.maxPlayers}
                    >
                        {roomInfo.playerCount >= roomInfo.maxPlayers ? 'Room Full' : 'Join Game'}
                    </button>
                </form>

                <div className="vertical-divider-container">
                    <div className="vertical-divider-line"></div>
                    <div className="vertical-divider-text">or</div>
                    <div className="vertical-divider-line"></div>
                </div>

                <button onClick={handleCreateNewGame} className="secondary-button">
                    Start new game
                </button>
            </div>
        </>
    );
};

export default RoomPreview; 